import { MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect } from "react"
import { Controller, ControllerRenderProps, useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"
import { digitalManagerApi } from "../../../../../services/api"
import {
  DiMaDetailsContent,
  DiMaDetailsContentProps,
} from "../../../../components/DiMaDetailsContent"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { FeedbackTopicGet, FeedbackTopicPut } from "../../../../models/FeedbackTopic"
import { useFeedbackTopicPriorityField } from "../../../../components/Helpers"

const validationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  priority: z.number().min(0, "Priority is required"),
})

const returnURL = `/feedbacks#topics`

export function FeedbackTopicDetails(props: {
  readonly feedbackTopic: FeedbackTopicGet
  readonly setFeedbackTopic: (updatedTopic: FeedbackTopicGet) => void
}) {
  const { user, isLoadingUser } = useContext(UserContext)
  const { feedbackTopic, setFeedbackTopic } = props
  const applicationId = feedbackTopic.application.id

  const formMethods = useForm<FeedbackTopicPut>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods

  useEffect(() => {
    reset({
      name: feedbackTopic.name,
      priority: feedbackTopic.priority,
    })
  }, [feedbackTopic.priority, feedbackTopic.name, reset])

  const onSubmit = useCallback(
    (values: FeedbackTopicPut) =>
      digitalManagerApi
        .put<FeedbackTopicGet>(`/feedbackTopics/${feedbackTopic.id}`, {
          name: values.name,
          priority: values.priority,
        })
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            toast.success("Feedback topic updated")
            setFeedbackTopic(res.data)
          }
        })
        .catch(() => {
          toast.error("Cannot update feedback topic")
          throw new Error("Cannot update feedback topic")
        }),
    [feedbackTopic.id, setFeedbackTopic]
  )

  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<FeedbackTopicPut, "name">) => (value: string) =>
      field.onChange(value),
    []
  )

  const renderTextFieldName = useCallback(
    ({ field }: { field: ControllerRenderProps<FeedbackTopicPut, "name"> }) => (
      <MUITextField
        id="name"
        type="text"
        size={32}
        name={field.name}
        value={field.value}
        onChange={handleTextFieldChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.name?.message === "string"
            ? { name: { message: errors.name.message } }
            : undefined
        }
      />
    ),
    [errors.name, handleTextFieldChange]
  )

  const renderPriorityTextField = useFeedbackTopicPriorityField<FeedbackTopicPut>({
    errors,
  })

  if (isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsContentProps: DiMaDetailsContentProps<FeedbackTopicPut> = {
    formMethods,
    inlineEdit: true,
    canWrite: hasUserAccessToApplicationFeature(
      user.authorizations.applications,
      applicationId,
      DimaFeatureShortNameEnum.FB,
      AccessLevel.Write
    ),
    listItems: [
      {
        id: 0,
        label: "Name",
        displayItem: <div className="title">{feedbackTopic.name}</div>,
        editItem: (
          <Controller name="name" control={control} render={renderTextFieldName} />
        ),
      },
      {
        id: 1,
        label: "Priority",
        displayItem: <div className="title">{feedbackTopic.priority}</div>,
        editItem: (
          <Controller
            name="priority"
            control={formMethods.control}
            render={renderPriorityTextField}
          />
        ),
      },
    ],
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DiMaDetailsContent {...detailsContentProps} onSubmit={onSubmit} />
    </form>
  )
}
